/* Progressive enhancement: without JS every specification section is visible. */
if (!customElements.get('milingi-movement-details')) {
  customElements.define('milingi-movement-details', class extends HTMLElement {
    #controller;
    #observer;
    #tabs = [];
    #panels = [];
    #ready = false;

    connectedCallback() {
      this.#controller?.abort();
      this.#controller = new AbortController();
      this.#ready = false;

      if (!this.#enhance()) {
        // Also supports definition before child markup is parsed or editor insertion.
        this.#observer = new MutationObserver(() => this.#enhance());
        this.#observer.observe(this, { childList: true, subtree: true });
      }
    }

    disconnectedCallback() {
      this.#controller?.abort();
      this.#observer?.disconnect();
      this.#ready = false;
    }

    #enhance() {
      if (this.#ready || !this.isConnected) return this.#ready;

      const tablist = this.querySelector('[data-mi-movement-tablist]');
      const tabs = Array.from(this.querySelectorAll('[data-mi-movement-tab]'));
      const panels = Array.from(this.querySelectorAll('[data-mi-movement-panel]'));

      // Do not hide fallback content unless every tab has its corresponding panel.
      if (!tablist || tabs.length !== 4 || panels.length !== tabs.length) return false;
      const orderedPanels = tabs.map((tab) => panels.find((panel) => panel.id === tab.getAttribute('aria-controls')));
      if (orderedPanels.some((panel) => !panel) || new Set(orderedPanels).size !== tabs.length) return false;

      this.#tabs = tabs;
      this.#panels = orderedPanels;
      this.#ready = true;
      this.#observer?.disconnect();

      this.#panels.forEach((panel, index) => {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', this.#tabs[index].id);
        panel.setAttribute('tabindex', '0');
      });

      const previousIndex = this.#tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      this.#activate(previousIndex < 0 ? 0 : previousIndex);
      this.setAttribute('data-enhanced', '');
      tablist.hidden = false;

      const options = { signal: this.#controller.signal };
      tablist.addEventListener('click', (event) => {
        const tab = event.target instanceof Element ? event.target.closest('[data-mi-movement-tab]') : null;
        const index = this.#tabs.indexOf(tab);
        if (index !== -1) this.#activate(index);
      }, options);

      tablist.addEventListener('keydown', (event) => {
        const index = this.#tabs.indexOf(event.target);
        if (index === -1 || event.altKey || event.ctrlKey || event.metaKey) return;

        let nextIndex;
        switch (event.key) {
          case 'ArrowRight':
            nextIndex = (index + 1) % this.#tabs.length;
            break;
          case 'ArrowLeft':
            nextIndex = (index - 1 + this.#tabs.length) % this.#tabs.length;
            break;
          case 'Home':
            nextIndex = 0;
            break;
          case 'End':
            nextIndex = this.#tabs.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        this.#activate(nextIndex);
        this.#tabs[nextIndex].focus();
      }, options);

      return true;
    }

    #activate(index) {
      this.#tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.setAttribute('aria-selected', String(active));
        tab.setAttribute('tabindex', active ? '0' : '-1');
        this.#panels[tabIndex].hidden = !active;
      });
    }
  });
}
